import { useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { useDataTaskProcessing } from '~/backend/connector-api/connector-components';
import { DataUploadItem } from '~/backend/connector-api/connector-schemas';
import { EnvironmentObjectIds } from '~/engine/data/Environment';
import {
    NewJobData,
    useJobManagement,
} from '~/hooks/workflows/useJobManagement';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import useDoseCacheUpdate from '../useDoseCacheUpdate';
import useStructureSetCacheUpdate from '../useStructureSetCacheUpdate';

type UseDataUploadTaskProps = {
    environmentObjectIds?: EnvironmentObjectIds;
    patientId: string;
};

/**
 * Transforms environmentObjectIds into a list of DataUploadItem objects.
 * @returns {DataUploadItem[]} The list of upload items.
 */
const transformEnvironmentToUploadItems = (
    environmentObjectIds: EnvironmentObjectIds,
    patientID: string
): DataUploadItem[] => {
    if (!environmentObjectIds) return [];
    const uploadItems: DataUploadItem[] = [];

    if (environmentObjectIds.primaryImage) {
        uploadItems.push(environmentObjectIds.primaryImage);
    }

    if (environmentObjectIds.blendImage) {
        uploadItems.push(environmentObjectIds.blendImage);
    }

    if (environmentObjectIds.structureSet) {
        uploadItems.push({
            sopInstanceUID: environmentObjectIds.structureSet.sopInstanceUID,
            patientID,
        });
    }

    if (environmentObjectIds.dose) {
        uploadItems.push({
            sopInstanceUID: environmentObjectIds.dose.sopInstanceUID,
            patientID,
        });
    }

    return uploadItems;
};

/**
 * Handles starting data upload tasks and monitoring their progress.
 * @param {UseDataUploadTaskProps} props - Contains environment object IDs and patient ID for the task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export default function useDataUploadTask({
    environmentObjectIds,
    patientId,
}: UseDataUploadTaskProps) {
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const { mutateAsync: startUploading } = useDataTaskProcessing();
    const updateCachedStructureSet = useStructureSetCacheUpdate();
    const updateCachedDose = useDoseCacheUpdate();
    const { addJob } = useJobManagement();

    /**
     * Starts the data upload task with the provided environmentObjectIds.
     * @param {string} action - The action header for the upload task.
     */
    const startUpload = async (action: string) => {
        if (isSubmitting) {
            return;
        }

        setIsSubmitting(true);
        try {
            if (!environmentObjectIds) {
                handleError('No active environment object available.');
                return;
            }
            if (environmentObjectIds?.structureSet?.sopInstanceUID) {
                await updateCachedStructureSet();
            }
            if (environmentObjectIds?.dose?.sopInstanceUID) {
                await updateCachedDose();
            }

            const uploadItems = transformEnvironmentToUploadItems(
                environmentObjectIds,
                patientId
            );

            const task = await startUploading({
                headers: {
                    action,
                },
                body: {
                    modelName: action,
                    uploadItems,
                },
            });

            const newJob: NewJobData = {
                name: `Data Upload+`,
                id: task.id,
                type: WorkflowTypes.DATA_UPLOAD,
                task: {
                    itemsAmount: uploadItems.length,
                    output: task,
                },
            };

            addJob(newJob);
            setWorkflowModalState((state) => ({
                ...state,
                open: false,
            }));
        } catch (error) {
            handleError(error, 'Failed to start data upload task.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return { startUpload, isSubmitting };
}
